import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sparkles } from "lucide-react";
import api, { apiErrorMsg, TIPOS_ORGAO } from "@/lib/api";
import { toast } from "sonner";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const initialForm = {
  municipio_nome: "",
  uf: "",
  tipo_orgao: "Câmara",
  cnpj: "",
  telefone: "",
  email: "",
  contato_nome: "",
  contato_cargo: "",
  contato_whatsapp: "",
  valor_estimado: "",
  observacoes: "",
};

export default function IndicarCelModal({ open, onClose, municipioInicial, onCreated }) {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        ...initialForm,
        municipio_nome: municipioInicial?.nome || "",
        uf: municipioInicial?.uf || "",
        tipo_orgao: municipioInicial?.tipo_orgao || "Câmara",
        cnpj: municipioInicial?.cnpj || "",
      });
    }
  }, [open, municipioInicial]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.municipio_nome.trim() || !form.uf) {
      toast.error("Informe município e UF.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, valor_estimado: Number(form.valor_estimado) || 0 };
      // Limpa strings vazias que devem ser null
      ["cnpj","telefone","email","contato_nome","contato_cargo","contato_whatsapp","observacoes"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      const r = await api.post("/cel/indicacoes", payload);
      toast.success(`Indicação criada · ${r.data.municipio_nome}/${r.data.uf}`);
      onCreated?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Não foi possível criar a indicação."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] p-0 gap-0 flex flex-col" data-testid="indicar-cel-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-sky-600" /> Indicar para CeL Consultoria
          </DialogTitle>
          <DialogDescription className="text-xs">
            Você indica um município e o time CeL cuida de todo o funil comercial de consultoria.
            Você acompanha o status em <b>Minhas Indicações</b>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-6 py-4 overflow-y-auto flex-1">
          <div className="md:col-span-2">
            <Label>Município *</Label>
            <Input
              value={form.municipio_nome}
              onChange={(e) => setField("municipio_nome", e.target.value)}
              placeholder="Ex.: Bento Gonçalves"
              data-testid="indicar-cel-municipio"
            />
          </div>
          <div>
            <Label>UF *</Label>
            <Select value={form.uf} onValueChange={(v) => setField("uf", v)}>
              <SelectTrigger data-testid="indicar-cel-uf"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent className="max-h-64">
                {UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo de órgão</Label>
            <Select value={form.tipo_orgao} onValueChange={(v) => setField("tipo_orgao", v)}>
              <SelectTrigger data-testid="indicar-cel-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPOS_ORGAO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>CNPJ</Label>
            <Input value={form.cnpj} onChange={(e) => setField("cnpj", e.target.value)} placeholder="00.000.000/0000-00" data-testid="indicar-cel-cnpj" />
          </div>
          <div>
            <Label>Telefone institucional</Label>
            <Input value={form.telefone} onChange={(e) => setField("telefone", e.target.value)} placeholder="(11) 99999-0000" />
          </div>
          <div className="md:col-span-2">
            <Label>E-mail institucional</Label>
            <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="contato@camara.gov.br" />
          </div>

          <div className="md:col-span-2 mt-2">
            <div className="text-xs uppercase font-bold tracking-wider text-slate-500 border-t pt-3 mb-1">Contato principal</div>
          </div>
          <div>
            <Label>Nome do contato</Label>
            <Input value={form.contato_nome} onChange={(e) => setField("contato_nome", e.target.value)} placeholder="Ex.: João Silva" data-testid="indicar-cel-contato-nome" />
          </div>
          <div>
            <Label>Cargo</Label>
            <Input value={form.contato_cargo} onChange={(e) => setField("contato_cargo", e.target.value)} placeholder="Ex.: Presidente" />
          </div>
          <div>
            <Label>WhatsApp do contato</Label>
            <Input value={form.contato_whatsapp} onChange={(e) => setField("contato_whatsapp", e.target.value)} placeholder="(11) 99999-0000" />
          </div>
          <div className="md:col-span-2">
            <Label>Observações</Label>
            <Textarea rows={3} value={form.observacoes} onChange={(e) => setField("observacoes", e.target.value)} placeholder="Contexto que ajuda o time CeL a abordar o cliente..." />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 flex-shrink-0 bg-white">
          <Button variant="outline" onClick={() => onClose?.()} disabled={busy} data-testid="indicar-cel-cancelar">Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="indicar-cel-submit">
            <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Enviando..." : "Enviar indicação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
